import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from "lucide-react";
import { Pagination } from "../../components/client";
import FilterBar from "../../components/shared/FilterBar";
import VendorPerformanceBadge from "../../components/client/VendorPerformanceBadge";
import MarketingListingCard from "../../components/home/MarketingListingCard";
import MarketingBrowseHeader from "../../components/home/MarketingBrowseHeader";
import MarketingBrowseEmpty from "../../components/home/MarketingBrowseEmpty";
import MarketingReveal from "../../components/home/MarketingReveal";
import { useServiceCategories } from "../../services/serviceCategoryService";
import { vendorService } from "../../services/vendorService";
import useUserLocation from "../../hooks/useUserLocation";
import { pageText } from "../../content/publicPages";
import { usePublicPage } from "../../hooks/usePublicPage";

const ClientBrowse = () => {
  const { saved, defaults } = usePublicPage("browse");
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceMin, setPriceMin] = useState(1);
  const [priceMax, setPriceMax] = useState(1000);
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");
  const [selectedRating, setSelectedRating] = useState("all");
  const [sortBy, setSortBy] = useState("none");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const { data: categoriesResp } = useServiceCategories({ per_page: 200 });
  const routerLocation = useLocation();
  const navigate = useNavigate();
  
  // Auto-detect and set location on mount
  const { userLocation, loading: locationLoading } = useUserLocation();
  
  // Auto-populate location when detected or updated
  useEffect(() => {
    if (userLocation) {
      // Use address if available, otherwise use city/state
      const locationString = userLocation.address || 
                            (userLocation.city && userLocation.state ? `${userLocation.city}, ${userLocation.state}` : null) ||
                            userLocation.city || 
                            userLocation.state || 
                            null;
      
      // Always update location if we have a valid location string
      if (locationString) {
        setLocation(locationString);
        console.log('ClientBrowse: Location set to:', locationString);
      }
    }
  }, [userLocation]);
  const catPayload = useMemo(() => {
    if (Array.isArray(categoriesResp?.data?.data))
      return categoriesResp.data.data;
    if (Array.isArray(categoriesResp?.data)) return categoriesResp.data;
    if (Array.isArray(categoriesResp)) return categoriesResp;
    return [];
  }, [categoriesResp]);
  const categories = [
    { value: "", label: "All Categories" },
    ...catPayload.map((c) => ({ value: c.id, label: c.category_name })),
  ];

  // Sync category from URL (?category=id) or from path (/category/:category)
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const qCat = params.get("category");
    
    // Also check if we're on a category route
    const pathParts = routerLocation.pathname.split('/');
    const categoryFromPath = pathParts.includes('category') && pathParts[pathParts.indexOf('category') + 1];
    
    const categoryId = qCat || categoryFromPath;
    
    if (!categoryId || categoryId === "all") return;

    // If already equals current selection, skip
    if (String(selectedCategory) === String(categoryId)) return;

    // Try match by id
    const match = (catPayload || []).find((c) => String(c.id) === String(categoryId));

    setSelectedCategory(match ? String(match.id) : "");
  }, [routerLocation.search, routerLocation.pathname, selectedCategory, catPayload]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    location,
    selectedCategory,
    priceMin,
    priceMax,
    sortBy,
    selectedRating,
    selectedPriceRange,
  ]);

  const sortOptions = [
    { value: "none", label: "No Sorting" },
    { value: "performance", label: "Best Match" },
    { value: "rating", label: "Highest Rated" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "reviews", label: "Most Reviews" },
    { value: "distance", label: "Distance" },
  ];

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm("");
    setLocation("");
    setSelectedCategory("");
    setPriceMin(1);
    setPriceMax(1000);
    setSelectedPriceRange("all");
    setSelectedRating("all");
    setSortBy("none");
    setCurrentPage(1);
    // Clear URL parameters
    navigate("/browse", { replace: true });
  };

  // Mock vendor data - in real app, this would come from API
  // eslint-disable-next-line no-unused-vars
  // const mockVendors = [
  //   {
  //     id: 1,
  //     name: 'Elite Photo Booth Co.',
  //     category: 'Photo & Video',
  //     rating: 4.9,
  //     reviewCount: 127,
  //     location: 'Los Angeles, CA',
  //     distance: '2.3 miles',
  //     price: '$299',
  //     priceRange: '$200-$400',
  //     description: 'Premium photo booth experiences with instant prints and digital galleries.',
  //     image: '/api/placeholder/300/200',
  //     services: ['Photo Booth', 'Photography', ''],
  //     availability: 'Available this weekend',
  //     featured: true
  //   },
  //   {
  //     id: 2,
  //     name: 'SoundWave DJ Services',
  //     category: 'Entertainment',
  //     rating: 4.8,
  //     reviewCount: 89,
  //     location: 'Miami, FL',
  //     distance: '5.1 miles',
  //     price: '$450',
  //     priceRange: '$300-$600',
  //     description: 'Professional DJ services for weddings, parties, and corporate events.',
  //     image: '/api/placeholder/300/200',
  //     services: ['DJ Services', 'Music', 'Sound System'],
  //     availability: 'Available next week',
  //     featured: false
  //   },
  //   {
  //     id: 3,
  //     name: 'Gourmet Catering Plus',
  //     category: 'Event Services',
  //     rating: 4.9,
  //     reviewCount: 156,
  //     location: 'New York, NY',
  //     distance: '1.8 miles',
  //     price: '$35/person',
  //     priceRange: '$25-$50/person',
  //     description: 'Exquisite catering services with customizable menus for any occasion.',
  //     image: '/api/placeholder/300/200',
  //     services: ['Catering', 'Event Planning', 'Menu Design'],
  //     availability: 'Available this month',
  //     featured: true
  //   },
  //   {
  //     id: 4,
  //     name: 'Luxury Limo Service',
  //     category: 'Transportation',
  //     rating: 4.7,
  //     reviewCount: 67,
  //     location: 'Chicago, IL',
  //     distance: '3.2 miles',
  //     price: '$150/hour',
  //     priceRange: '$100-$200/hour',
  //     description: 'Premium limousine and party bus services for special occasions.',
  //     image: '/api/placeholder/300/200',
  //     services: ['Limousine', 'Party Bus', 'Airport Transfer'],
  //     availability: 'Available today',
  //     featured: false
  //   },
  //   {
  //     id: 5,
  //     name: 'Beauty Studio Pro',
  //     category: 'Beauty & Wellness',
  //     rating: 4.8,
  //     reviewCount: 94,
  //     location: 'San Francisco, CA',
  //     distance: '4.5 miles',
  //     price: '$120',
  //     priceRange: '$80-$150',
  //     description: 'Professional hair styling and makeup services for events.',
  //     image: '/api/placeholder/300/200',
  //     services: ['Hair Styling', 'Makeup', 'Bridal Services'],
  //     availability: 'Available this weekend',
  //     featured: false
  //   }
  // ];

  // Build params with location-based filtering
  const params = {
    ...(searchTerm && { search: searchTerm }),
    ...(selectedCategory && { service_category_id: selectedCategory }),
    ...(priceMin && priceMin > 1 && { min_price: priceMin }),
    ...(priceMax && priceMax < 1000 && { max_price: priceMax }),
    ...(selectedRating && selectedRating !== "all" && { min_rating: parseFloat(selectedRating.replace('+', '')) }),
    per_page: 12,
    page: currentPage,
  };
  
  // Add location-based filtering and sorting
  // Use location for sorting by default, but don't filter by radius unless explicitly needed
  if (userLocation?.latitude && userLocation?.longitude) {
    params.latitude = userLocation.latitude;
    params.longitude = userLocation.longitude;
    // Don't send radius parameter - this way backend won't filter by distance
    // Location will only be used for sorting, not filtering
    // This ensures all vendors show up, but sorted by distance
    
    // Use distance sorting when location is available
    if (sortBy === "distance" || sortBy === "none") {
      params.sort_by = "distance";
      params.sort_order = "asc";
    }
  } else if (location) {
    // Fallback to city/state if coordinates not available
    const locationParts = location.split(', ');
    if (locationParts.length >= 2) {
      params.city = locationParts[0];
      params.state = locationParts[1];
    } else {
      params.city = location;
    }
  }
  
  // Handle other sort options
  if (sortBy !== "none" && sortBy !== "distance") {
    params.sort_by =
      sortBy === "rating"
        ? "rating"
        : sortBy === "performance"
        ? "performance"
        : sortBy === "price_low" || sortBy === "price_high"
        ? "price"
        : "created_at";
    params.sort_order = sortBy === "price_low" ? "asc" : "desc";
  }
  
  // Debug logging
  // console.log("API Params:", params);
  // console.log("Selected Category:", selectedCategory);
  // console.log("Price Range:", priceMin, priceMax);

  // Fetch vendors
  const { data: vendorsResp, isLoading } = useQuery({
    queryKey: ['public-vendors', params],
    queryFn: () => vendorService.getVendors(params),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true
  });
  
  useEffect(() => {
    if (isLoading) {
      setLoading(true);
      return;
    }
    
    // console.log("API Response:", vendorsResp);
    
    const payload = vendorsResp?.data?.data || vendorsResp?.data || [];
    const paginationData = vendorsResp?.data || {};
    
    if (!payload || payload.length === 0) {
      console.log("No vendors found in response");
      setVendors([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 12,
        total: 0,
      });
      setLoading(false);
      return;
    }
    
    // Update pagination state
    setPagination({
      current_page: paginationData.current_page || 1,
      last_page: paginationData.last_page || 1,
      per_page: paginationData.per_page || 12,
      total: paginationData.total || 0,
    });
    const truncate = (str, n = 80) => {
      if (!str) return "";
      const s = String(str);
      return s.length > n ? s.slice(0, n) + "…" : s;
    };
    // Map vendor data
    const mapped = payload.map((v) => ({
      id: v.id,
      name: v.business_name || "Vendor",
      category: v.business_type || "Business",
      categoryId: String(v.service_category_id || ""),
      rating: Number(v.average_rating || 0),
      performanceScore: Number(v.performance_score || 0),
      platformBookingsCount: Number(v.platform_bookings_count || 0),
      reviewCount: v.total_reviews || 0,
      location: [v.city, v.state].filter(Boolean).join(", "),
      distance: "",
      price: "View Services",
      priceRange: "",
      description: truncate(v.description || v.bio || "", 80),
      image: v.image_url || v.user?.profile_image_url || "",
      services: v.service_categories?.map(cat => cat.category_name).slice(0, 3) || [],
      serviceCount: v.service_count || 0,
      availability: v.is_online ? "Online now" : "",
      featured: !!v.is_featured,
      vendorId: v.id,
      phone: v.phone_number || "",
      email: v.email || "",
      businessName: v.business_name || "Vendor"
    }));
    setVendors(mapped);
    setLoading(false);
  }, [vendorsResp, isLoading]);

  // Filter vendors - only by search term, not by location
  // Location filtering is handled by the backend API
  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch =
      !searchTerm ||
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    // Featured vendors always at the top
    if (a.featured !== b.featured) {
      return b.featured ? 1 : -1;
    }
    
    // Then sort by selected criteria
    switch (sortBy) {
      case "rating":
        return b.rating - a.rating;
      case "price_low":
        return (
          parseInt(a.price.replace(/[^0-9]/g, "") || "0") -
          parseInt(b.price.replace(/[^0-9]/g, "") || "0")
        );
      case "price_high":
        return (
          parseInt(b.price.replace(/[^0-9]/g, "") || "0") -
          parseInt(a.price.replace(/[^0-9]/g, "") || "0")
        );
      case "reviews":
        return b.reviewCount - a.reviewCount;
      case "performance":
        return b.performanceScore - a.performanceScore;
      case "none":
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-m-canvas-light">
      {/* Mobile: Title Section */}
      <div className="border-b border-m-border-light bg-m-surface md:hidden">
        <div className="page-container py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              <span>Back</span>
            </Link>
            <h1 className="mx-4 flex-1 text-center text-base font-medium tracking-tight text-m-ink">
              Browse vendors
            </h1>
            <div className="w-12"></div>
          </div>
        </div>
              </div>
              
      {/* Mobile: Sticky Filter Bar */}
      <div className="sticky top-16 z-30 border-b border-m-border-light bg-m-surface/95 backdrop-blur-sm md:hidden">
        <div className="page-container py-3">
          <FilterBar
            selectedPriceRange={selectedPriceRange}
            setSelectedPriceRange={setSelectedPriceRange}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            location={location}
            setLocation={setLocation}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOptions={sortOptions}
            clearAllFilters={clearAllFilters}
            showCategoryFilter={true}
            priceMin={priceMin}
            setPriceMin={setPriceMin}
            priceMax={priceMax}
            setPriceMax={setPriceMax}
                  />
                </div>
              </div>

      {/* Desktop: One Line Header - Back | Filters | Count (Sticky) */}
      <div className="sticky top-16 z-30 hidden border-b border-m-border-light bg-m-surface/95 backdrop-blur-sm md:block">
        <div className="page-container py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Back Button */}
            <Link
              to="/"
              className="inline-flex items-center whitespace-nowrap text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>Back</span>
            </Link>

            <div className="flex-1"></div>

            {/* Filter Bar on Right */}
            <div className="flex items-center gap-4">
              <FilterBar
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                showMoreFilters={showMoreFilters}
                setShowMoreFilters={setShowMoreFilters}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                location={location}
                setLocation={setLocation}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                categories={categories}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOptions={sortOptions}
                clearAllFilters={clearAllFilters}
                showCategoryFilter={true}
                priceMin={priceMin}
                setPriceMin={setPriceMin}
                priceMax={priceMax}
                setPriceMax={setPriceMax}
              />

              {/* Results Count */}
              <p className="whitespace-nowrap text-sm font-normal text-m-ink-muted">
                {(loading || locationLoading) ? "Loading…" : `${sortedVendors.length} vendors`}
              </p>
            </div>
                </div>
              </div>
            </div>

      <div className="page-container px-4 py-8 md:px-8 md:py-14">
        <p className="mb-5 text-sm font-normal text-m-ink-muted md:hidden">
          {(loading || locationLoading) ? "Loading…" : `${sortedVendors.length} vendors found`}
        </p>

        <MarketingBrowseHeader
          eyebrow={pageText(saved.eyebrow, defaults.eyebrow)}
          title={pageText(saved.title, defaults.title)}
          description={pageText(saved.description, defaults.description)}
          countLabel={(loading || locationLoading) ? 'Loading…' : `${sortedVendors.length} vendors`}
        />

        <MarketingReveal className="relative flex gap-8">
          <div className="flex-1">
            {(loading || locationLoading) ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="animate-pulse overflow-hidden">
                    <div className="h-56 rounded-m-card bg-m-border-light" />
                    <div className="mt-3 space-y-2">
                      <div className="h-4 w-3/4 rounded bg-m-border-light" />
                      <div className="h-3 w-1/2 rounded bg-m-border-light" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sortedVendors.length > 0 ? (
              <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {sortedVendors.map((vendor) => (
                  <MarketingListingCard
                    key={vendor.id}
                    href={`/vendor/${vendor.id}`}
                    image={vendor.image}
                    title={vendor.name}
                    subtitle={vendor.category}
                    location={vendor.location || 'Location not specified'}
                    rating={vendor.rating}
                    meta={`${vendor.serviceCount} ${vendor.serviceCount === 1 ? 'service' : 'services'}`}
                    reviewCount={vendor.reviewCount}
                    featured={vendor.featured}
                    imageOverlay={
                      <VendorPerformanceBadge
                        score={vendor.performanceScore}
                        bookingsCount={vendor.platformBookingsCount}
                        className="border-white/70 bg-white/95 shadow-sm"
                      />
                    }
                  />
                ))}
              </div>
            ) : (
              <MarketingBrowseEmpty
                title={pageText(saved.empty_title, defaults.empty_title)}
                description={pageText(saved.empty_body, defaults.empty_body)}
                onClear={clearAllFilters}
                secondaryHref="/"
                secondaryLabel="Back to home"
              />
            )}

            {!locationLoading && !loading && sortedVendors.length > 0 ? (
              <Pagination
                currentPage={pagination.current_page}
                lastPage={pagination.last_page}
                perPage={pagination.per_page}
                total={pagination.total}
                onPageChange={setCurrentPage}
              />
            ) : null}
          </div>
        </MarketingReveal>
      </div>
    </div>
  );
};

export default ClientBrowse;
